import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTodos, Todo } from '../../crm-todos/useData';
import { useTheme } from '../../../contexts/ThemeContext';

export interface TodosCalendarWidgetProps {
  currentDate?: Date;
}

const TodosCalendarWidget: React.FC<TodosCalendarWidgetProps> = ({ currentDate = new Date() }) => {
  const navigate = useNavigate();
  const { colors } = useTheme();
  const { data: todos = [], isLoading } = useTodos();

  const [viewDate, setViewDate] = React.useState(currentDate);

  const year = viewDate.getFullYear();
  const month = viewDate.getMonth();

  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const daysInMonth = lastDay.getDate();
  const startingDayOfWeek = firstDay.getDay();

  const todosByDate = React.useMemo(() => {
    const map = new Map<string, Todo[]>();
    (todos || []).forEach((todo: Todo) => {
      if (todo.todoDueDate) {
        const dateKey = todo.todoDueDate.slice(0, 10);
        const existing = map.get(dateKey) || [];
        map.set(dateKey, [...existing, todo]);
      }
    });
    return map;
  }, [todos]);

  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const goToPreviousMonth = () => {
    setViewDate(new Date(year, month - 1, 1));
  };

  const goToNextMonth = () => {
    setViewDate(new Date(year, month + 1, 1));
  };

  const goToToday = () => {
    setViewDate(new Date());
  };

  const isToday = (day: number) => {
    const today = new Date();
    return (
      day === today.getDate() &&
      month === today.getMonth() &&
      year === today.getFullYear()
    );
  };

  const getTodosForDay = (day: number) => {
    const dateKey = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return todosByDate.get(dateKey) || [];
  };

  if (isLoading) {
    return (
      <div className={`${colors.surface} rounded-xl shadow-sm p-6 border ${colors.border}`}>
        <div className="h-64 flex items-center justify-center">
          <div className="animate-pulse text-gray-400">Loading calendar...</div>
        </div>
      </div>
    );
  }

  return (
    <div className={`${colors.surface} rounded-xl shadow-sm p-6 border ${colors.border}`}>
      <div className="flex items-center justify-between mb-4">
        <h2 className={`text-lg font-semibold ${colors.text}`}>Todos</h2>
        <button
          onClick={() => navigate('/todos_monthly_view')}
          className="text-sm text-amber-600 hover:text-amber-700 dark:text-amber-400 dark:hover:text-amber-300 font-medium"
        >
          View Full
        </button>
      </div>

      <div className="mb-4 flex items-center justify-between">
        <button
          onClick={goToPreviousMonth}
          className={`p-2.5 sm:p-2 min-h-[44px] min-w-[44px] sm:min-h-0 sm:min-w-0 flex items-center justify-center rounded-lg transition-colors touch-manipulation ${colors.interactiveHover}`}
          aria-label="Previous month"
        >
          <svg className="w-5 h-5 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <div className="flex items-center gap-2">
          <h3 className={`text-base font-semibold ${colors.text}`}>
            {monthNames[month]} {year}
          </h3>
          <button
            onClick={goToToday}
            className="px-3 py-1 text-xs text-amber-600 hover:bg-amber-50 dark:text-amber-400 dark:hover:bg-amber-900/20 rounded-md transition-colors"
          >
            Today
          </button>
        </div>
        <button
          onClick={goToNextMonth}
          className={`p-2.5 sm:p-2 min-h-[44px] min-w-[44px] sm:min-h-0 sm:min-w-0 flex items-center justify-center rounded-lg transition-colors touch-manipulation ${colors.interactiveHover}`}
          aria-label="Next month"
        >
          <svg className="w-5 h-5 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>

      <div className="overflow-x-auto -mx-1 px-1 sm:mx-0 sm:px-0 touch-pan-x" style={{ minWidth: 0 }}>
        <div className="grid grid-cols-7 gap-0.5 sm:gap-1 mb-2 min-w-[240px] w-max max-w-full mx-auto">
          {dayNames.map((day) => (
            <div key={day} className="text-center text-[10px] sm:text-xs font-medium text-gray-500 dark:text-gray-400 py-0.5 sm:py-1">
              {day}
            </div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-0.5 sm:gap-1 min-w-[240px] w-max max-w-full mx-auto">
        {Array.from({ length: startingDayOfWeek }).map((_, index) => (
          <div key={`empty-${index}`} className="aspect-square min-w-[28px] sm:min-w-[32px]" />
        ))}
        {Array.from({ length: daysInMonth }).map((_, index) => {
          const day = index + 1;
          const dayTodos = getTodosForDay(day);
          const today = isToday(day);

          return (
            <div
              key={day}
              onClick={() => navigate('/todos_monthly_view')}
              className={`aspect-square min-w-[28px] sm:min-w-[32px] p-0.5 sm:p-1 border rounded cursor-pointer transition-colors ${
                today
                  ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-300 dark:border-amber-700'
                  : `${colors.border} hover:bg-gray-50 dark:hover:bg-gray-800/50`
              }`}
            >
              <div className={`text-[10px] sm:text-xs font-medium mb-0.5 sm:mb-1 ${today ? 'text-amber-600 dark:text-amber-400' : colors.text}`}>
                {day}
              </div>
              {dayTodos.length > 0 && (
                <div className="space-y-0.5">
                  {dayTodos.slice(0, 2).map((todo, idx) => (
                    <div
                      key={todo.todoId ?? idx}
                      className="h-1.5 bg-amber-500 dark:bg-amber-600 rounded text-[0px]"
                      title={todo.todoDescr || 'Todo'}
                    />
                  ))}
                  {dayTodos.length > 2 && (
                    <div className="text-[0.6rem] text-gray-500 dark:text-gray-400 font-medium">
                      +{dayTodos.length - 2}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
        </div>
      </div>
    </div>
  );
};

export default TodosCalendarWidget;
